import type { AuthError } from '@supabase/supabase-js'
import type {
  AdaptivePracticeCatalog,
  AppUser,
  BackendPort,
  GradeRequest,
  GradeResult,
  LearningOverview,
  LearningInsights,
  MasteredQuestion,
  Membership,
  MembershipPlan,
  NextQuestionRequest,
  PublicQuestion,
  PracticePreset,
  PracticeSessionStart,
  PracticeSessionState,
  QuestionFilterCounts,
  QuestionType,
  WrongAnswer,
} from '../types'
import { Upload } from 'tus-js-client'
import { guardDeploymentOperation, ensureDeploymentFeature, isCloudflareDeployment } from '../deployment'
import { supabase, supabaseAnonKey, supabaseConfigured, supabasePublicUrl } from './client'

function ensureConfigured() {
  if (!supabaseConfigured) throw new Error('尚未配置 Supabase。请复制环境变量样例并填写项目 URL 与 anon key。')
}

function throwIfError(error: AuthError | Error | null) {
  if (error) throw new Error(error.message)
}

async function functionError(error: Error & { context?: unknown }): Promise<Error> {
  const response = error.context
  if (response instanceof Response) {
    try {
      const payload = await response.clone().json()
      if (payload?.message || payload?.code) {
        const requestHint = payload.requestId ? `（请求 ${payload.requestId}）` : ''
        return new Error(`${payload.message || payload.code}${requestHint}`)
      }
    } catch {
      // Keep the SDK message when an upstream proxy did not return JSON.
    }
  }
  return new Error(error.message)
}

async function invoke<T>(name: string, body: Record<string, unknown>): Promise<T> {
  ensureConfigured()
  await guardDeploymentOperation(name, body)
  const { data, error } = await supabase.functions.invoke(name, { body })
  if (error) throw await functionError(error)
  if (data?.code) throw new Error(data.message || data.code)
  return data?.data as T
}

async function invokeForm<T>(name: string, body: FormData): Promise<T> {
  ensureConfigured()
  const { data, error } = await supabase.functions.invoke(name, { body })
  if (error) throw await functionError(error)
  if (data?.code) throw new Error(data.message || data.code)
  return data?.data as T
}

function mediaMime(file: File) {
  if (file.type) return file.type.split(';')[0]
  const extension = file.name.toLowerCase().split('.').pop()
  return (
    (
      {
        mp3: 'audio/mpeg',
        m4a: 'audio/mp4',
        wav: 'audio/wav',
        ogg: 'audio/ogg',
        webm: 'video/webm',
        mp4: 'video/mp4',
        mov: 'video/quicktime',
      } as Record<string, string>
    )[extension || ''] || 'application/octet-stream'
  )
}

async function resolveUser(): Promise<AppUser | null> {
  ensureConfigured()
  const { data } = await supabase.auth.getSession()
  const authUser = data.session?.user
  if (!authUser) return null
  const { data: profile, error } = await supabase.from('profiles').select('*').eq('id', authUser.id).single()
  if (error) throw new Error(error.message)
  const role = ['super_admin', 'admin'].includes(authUser.app_metadata?.role) ? authUser.app_metadata.role : 'user'
  return {
    id: authUser.id,
    email: authUser.email || '',
    nickname: profile.nickname,
    avatarUrl: profile.avatar_url || undefined,
    phone: profile.phone || undefined,
    birthDate: profile.birth_date || undefined,
    gradeLevel: profile.grade_level || undefined,
    role,
    status: profile.account_status,
    mustChangePassword: profile.must_change_password,
  }
}

export const backend: BackendPort = {
  isConfigured: () => supabaseConfigured,
  getSessionUser: resolveUser,
  onAuthChanged(callback) {
    const { data } = supabase.auth.onAuthStateChange(() => callback())
    return () => data.subscription.unsubscribe()
  },
  async signIn(email, password) {
    ensureConfigured()
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    throwIfError(error)
  },
  async signUp(email, password, nickname) {
    ensureConfigured()
    const { error } = await supabase.auth.signUp({ email, password, options: { data: { nickname } } })
    throwIfError(error)
  },
  async resendConfirmation(email) {
    ensureConfigured()
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: { emailRedirectTo: `${window.location.origin}/login` },
    })
    // The UI always displays a neutral result so account existence is never disclosed.
    if (error && !/already|confirmed|not found/i.test(error.message)) throw new Error(error.message)
  },
  async sendOtp(email) {
    ensureConfigured()
    const { error } = await supabase.auth.signInWithOtp({ email, options: { shouldCreateUser: false } })
    throwIfError(error)
  },
  async verifyOtp(email, token) {
    ensureConfigured()
    const { error } = await supabase.auth.verifyOtp({ email, token, type: 'email' })
    throwIfError(error)
  },
  async resetPassword(email) {
    ensureConfigured()
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/profile?recovery=1`,
    })
    throwIfError(error)
  },
  async signOut() {
    ensureConfigured()
    const { error } = await supabase.auth.signOut({ scope: 'local' })
    throwIfError(error)
  },
  async updateProfile(input) {
    ensureConfigured()
    if (input.email || input.password) {
      const { error } = await supabase.auth.updateUser({ email: input.email, password: input.password })
      throwIfError(error)
    }
    if (
      input.nickname !== undefined ||
      input.phone !== undefined ||
      input.birthDate !== undefined ||
      input.gradeLevel !== undefined
    ) {
      const { data: session } = await supabase.auth.getUser()
      if (!session.user) throw new Error('请先登录。')
      const profileUpdate: Record<string, string | null> = {}
      if (input.nickname !== undefined) profileUpdate.nickname = input.nickname.trim()
      if (input.phone !== undefined) profileUpdate.phone = input.phone
      if (input.birthDate !== undefined) profileUpdate.birth_date = input.birthDate
      if (input.gradeLevel !== undefined) profileUpdate.grade_level = input.gradeLevel
      const { error } = await supabase.from('profiles').update(profileUpdate).eq('id', session.user.id)
      if (error) throw new Error(error.message)
    }
  },
  async uploadAvatar(file) {
    ensureConfigured()
    const { data: auth } = await supabase.auth.getUser()
    if (!auth.user) throw new Error('请先登录。')
    const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg'
    const path = `${auth.user.id}/avatar-${Date.now()}.${extension}`
    const { error } = await supabase.storage
      .from('avatars')
      .upload(path, file, { upsert: true, contentType: file.type })
    if (error) throw new Error(error.message)
    const { data } = supabase.storage.from('avatars').getPublicUrl(path)
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ avatar_url: data.publicUrl })
      .eq('id', auth.user.id)
    if (updateError) throw new Error(updateError.message)
    return data.publicUrl
  },
  async getMembership() {
    ensureConfigured()
    const { data, error } = await supabase.rpc('get_effective_membership')
    if (error) throw new Error(error.message)
    return (Array.isArray(data) ? data[0] : data) as Membership
  },
  async getOverview(days = 30) {
    ensureConfigured()
    const { data, error } = await supabase.rpc('get_learning_overview', { p_days: days })
    if (error) throw new Error(error.message)
    return data as LearningOverview
  },
  async getLearningInsights(input) {
    ensureConfigured()
    const { data, error } = await supabase.rpc('get_learning_insights', {
      p_from: input.from,
      p_to: input.to,
      p_timezone: input.timezone,
    })
    if (error) throw new Error(error.message)
    return data as LearningInsights
  },
  nextQuestion(input: NextQuestionRequest) {
    return invoke<PublicQuestion>('question-next', input as unknown as Record<string, unknown>)
  },
  questionMediaUrl(questionId, purpose) {
    return invoke('question-media-url', { questionId, purpose })
  },
  async questionFilterCounts(questionType: QuestionType, source = {}) {
    ensureConfigured()
    const { data, error } = await supabase.rpc('get_question_filter_counts', {
      p_question_type: questionType,
      p_source_kind: source.sourceKind || null,
      p_source_package_codes: source.sourcePackageCodes || [],
    })
    if (error) throw new Error(error.message)
    const raw = (data || {}) as Partial<QuestionFilterCounts>
    return {
      modes: {
        random: Number(raw.modes?.random || 0),
        special: Number(raw.modes?.special || 0),
        wrong: Number(raw.modes?.wrong || 0),
      },
      difficulties: {
        random: raw.difficulties?.random || {},
        special: raw.difficulties?.special || {},
        wrong: raw.difficulties?.wrong || {},
      },
      translationDirections: {
        random: {
          zh_to_en: Number(raw.translationDirections?.random?.zh_to_en || 0),
          en_to_zh: Number(raw.translationDirections?.random?.en_to_zh || 0),
        },
        special: {
          zh_to_en: Number(raw.translationDirections?.special?.zh_to_en || 0),
          en_to_zh: Number(raw.translationDirections?.special?.en_to_zh || 0),
        },
        wrong: {
          zh_to_en: Number(raw.translationDirections?.wrong?.zh_to_en || 0),
          en_to_zh: Number(raw.translationDirections?.wrong?.en_to_zh || 0),
        },
      },
      sources: {
        kinds: raw.sources?.kinds || [],
        packages: raw.sources?.packages || [],
      },
    }
  },
  gradeAnswer(input: GradeRequest) {
    return invoke<GradeResult>('grade-answer', input as unknown as Record<string, unknown>)
  },
  assessPronunciation(recordingId, questionId) {
    return invoke('pronunciation-assess', { recordingId, questionId })
  },
  getPracticeCatalog(source = {}) {
    return invoke<AdaptivePracticeCatalog>('practice-session', { action: 'catalog', ...source })
  },
  startPracticeSession(input: PracticeSessionStart) {
    return invoke<PracticeSessionState>('practice-session', { action: 'start', ...input })
  },
  resumePracticeSession(sessionId: string) {
    return invoke<PracticeSessionState>('practice-session', { action: 'resume', sessionId })
  },
  answerPracticeSession(input) {
    const { sessionItemId, ...answer } = input
    return invoke<{ grade: GradeResult; session: PracticeSessionState }>('practice-session', {
      action: 'answer',
      ...answer,
      itemId: sessionItemId,
    })
  },
  async abandonPracticeSession(sessionId: string) {
    await invoke('practice-session', { action: 'abandon', sessionId })
  },
  listPracticePresets() {
    return invoke<PracticePreset[]>('practice-session', { action: 'preset-list' })
  },
  savePracticePreset(input) {
    return invoke<PracticePreset>('practice-session', { action: 'preset-save', ...input })
  },
  async deletePracticePreset(id: string) {
    await invoke('practice-session', { action: 'preset-delete', presetId: id })
  },
  transcribeAudio(file, options) {
    const form = new FormData()
    form.set('audio', file)
    form.set('purpose', options.purpose)
    form.set('durationMs', String(options.durationMs))
    if (options.questionId) form.set('questionId', options.questionId)
    return invokeForm<{
      transcript: string
      recordingId?: string
      provider?: string
      model?: string
      durationMs?: number
    }>('speech-transcribe', form)
  },
  listRecordings(page = 1, pageSize = 20) {
    return invoke('recordings', { action: 'list', page, pageSize })
  },
  async recordingUrl(recordingId) {
    const result = await invoke<{ url: string }>('recordings', { action: 'signed-url', recordingId })
    return result.url
  },
  async deleteRecording(recordingId) {
    await invoke('recordings', { action: 'delete', recordingId })
  },
  redeemCode(code: string) {
    return invoke<{ expires_at: string; plan_id: string }>('redeem-code', { code })
  },
  listWrongAnswers(filters = {}) {
    return invoke<WrongAnswer[]>('wrong-answers-export', { action: 'list', ...filters })
  },
  async updateWrongAnswer(id, action, mastered) {
    await invoke('wrong-answers-export', { action, id, mastered })
  },
  listMasteredQuestions() {
    return invoke<MasteredQuestion[]>('practice-session', { action: 'mastery-list' })
  },
  async masterQuestion(questionId) {
    await invoke('practice-session', { action: 'mastery-mark', questionId })
  },
  async unmasterQuestion(questionId) {
    await invoke('practice-session', { action: 'mastery-unmark', questionId })
  },
  exportWrongAnswers() {
    return invoke<WrongAnswer[]>('wrong-answers-export', { action: 'export' })
  },
  async wrongAnswerAudioUrl(id) {
    const result = await invoke<{ url: string }>('wrong-answers-export', { action: 'audio-url', id })
    return result.url
  },
  async listPlans() {
    ensureConfigured()
    const { data, error } = await supabase
      .from('membership_plans')
      .select('*')
      .eq('is_active', true)
      .order('price_cents')
    if (error) throw new Error(error.message)
    return data as MembershipPlan[]
  },
  admin<T>(action: string, payload = {}) {
    return invoke<T>('admin-api', { action, ...payload })
  },
  adminAi<T>(action: string, payload = {}) {
    return invoke<T>('admin-ai-config', { action, ...payload })
  },
  generateQuestions(input) {
    return invoke('admin-question-generate', input as unknown as Record<string, unknown>)
  },
  adminQuestionMedia(input) {
    const form = new FormData()
    form.set('action', input.action)
    if (input.questionId) form.set('questionId', input.questionId)
    if (input.questionIds) form.set('questionIds', JSON.stringify(input.questionIds))
    if (input.audio) form.set('audio', input.audio)
    if (input.text) form.set('text', input.text)
    return invokeForm('admin-question-media', form)
  },
  listTypingProjects(filters = {}) {
    return invoke('typing-training', { action: 'catalog-list', ...filters })
  },
  getTypingProject(projectId, offset, limit = 100) {
    return invoke('typing-training', {
      action: 'detail',
      projectId,
      limit,
      ...(offset === undefined ? {} : { offset }),
    })
  },
  getTypingSessionItems(projectId, offset, limit = 100) {
    return invoke('typing-training', { action: 'session-items', projectId, offset, limit })
  },
  getTypingCatalogIndex(input) {
    return invoke('typing-training', { action: 'catalog-index', ...input })
  },
  saveTypingProject(input) {
    return invoke('typing-training', { action: 'save-project', ...input })
  },
  listTypingCollections(sourceItemId) {
    return invoke('typing-training', {
      action: 'collection-list',
      ...(sourceItemId ? { sourceItemId } : {}),
    })
  },
  addTypingItemToCollection(collectionId, sourceItemId) {
    return invoke('typing-training', { action: 'collection-add-item', collectionId, sourceItemId })
  },
  async removeTypingItemFromCollection(collectionId, sourceItemId) {
    await invoke('typing-training', { action: 'collection-remove-item', collectionId, sourceItemId })
  },
  saveTypingItem(input) {
    return invoke('typing-training', { action: 'item-save', ...input })
  },
  async deleteTypingItem(projectId, itemId) {
    await invoke('typing-training', { action: 'item-delete', projectId, itemId })
  },
  async deleteTypingProject(projectId) {
    await invoke('typing-training', { action: 'delete-project', projectId })
  },
  previewTypingImport(input) {
    return invoke('typing-training', { action: 'import-preview', ...input })
  },
  commitTypingImport(input) {
    return invoke('typing-training', { action: 'import-commit', ...input })
  },
  submitTypingAttempt(input) {
    return invoke('typing-training', { action: 'attempt', ...input })
  },
  async typingAudioUrl(itemId) {
    const result = await invoke<{ url: string }>('typing-training', { action: 'audio-url', itemId })
    return result.url
  },
  getTypingPreferences() {
    return invoke('typing-training', { action: 'preferences' })
  },
  saveTypingPreferences(preferences) {
    return invoke('typing-training', { action: 'preferences-save', preferences })
  },
  getTypingCloudSpeechOptions() {
    return invoke(isCloudflareDeployment ? 'typing-speech' : 'typing-training', { action: 'speech-options' })
  },
  previewTypingCloudVoice(input) {
    return invoke(isCloudflareDeployment ? 'typing-speech' : 'typing-training', { action: 'speech-preview', ...input })
  },
  submitTypingWordReview(input) {
    return invoke('typing-training', { action: 'word-review', ...input })
  },
  async updateTypingWordState(input) {
    await invoke('typing-training', { action: 'word-state', ...input })
  },
  typingWordAudioUrl(itemId, accent, voiceId, speechRate, exampleText) {
    return invoke(isCloudflareDeployment ? 'typing-speech' : 'typing-training', {
      action: 'word-audio-url',
      itemId,
      accent,
      ...(voiceId ? { voiceId } : {}),
      ...(speechRate === undefined ? {} : { speechRate }),
      ...(exampleText === undefined ? {} : { exampleText }),
    })
  },
  getTypingImportStatus(provider = 'typewords') {
    return invoke('typing-training', { action: 'import-status', provider })
  },
  listMediaProjects() {
    return invoke('media-learning', { action: 'list' })
  },
  getMediaProject(projectId) {
    return invoke('media-learning', { action: 'detail', projectId })
  },
  reserveMediaUpload(input) {
    const detected = mediaMime(input.file)
    const mimeType = detected === 'video/webm' && input.mediaKind === 'audio' ? 'audio/webm' : detected
    return invoke('media-learning', {
      action: 'reserve-upload',
      title: input.title,
      filename: input.file.name,
      mimeType,
      sizeBytes: input.file.size,
      mediaKind: input.mediaKind,
      language: input.language || 'en',
      rightsConfirmed: input.rightsConfirmed,
      translationEnabled: input.translationEnabled ?? false,
    })
  },
  async uploadMediaFile(file, reservation, onProgress) {
    ensureConfigured()
    await ensureDeploymentFeature('media_upload')
    const { data } = await supabase.auth.getSession()
    const accessToken = data.session?.access_token
    if (!accessToken) throw new Error('登录状态已失效，请重新登录。')
    await new Promise<void>((resolve, reject) => {
      const upload = new Upload(file, {
        endpoint: `${supabasePublicUrl.replace(/\/$/, '')}/storage/v1/upload/resumable`,
        headers: { authorization: `Bearer ${accessToken}`, apikey: supabaseAnonKey },
        uploadDataDuringCreation: true,
        removeFingerprintOnSuccess: true,
        chunkSize: 6 * 1024 * 1024,
        retryDelays: [0, 1000, 3000, 5000, 10000],
        metadata: {
          bucketName: 'learning-media',
          objectName: reservation.objectPath,
          contentType: mediaMime(file),
          cacheControl: '3600',
        },
        onError(error) {
          reject(new Error(`媒体上传失败：${error.message}`))
        },
        onProgress(uploaded, total) {
          onProgress?.(total ? Math.round((uploaded / total) * 100) : 0)
        },
        onSuccess() {
          resolve()
        },
      })
      upload
        .findPreviousUploads()
        .then((previous) => {
          if (previous[0]) upload.resumeFromPreviousUpload(previous[0])
          upload.start()
        })
        .catch(reject)
    })
  },
  completeMediaUpload(projectId) {
    return invoke('media-learning', { action: 'upload-complete', projectId })
  },
  retryMediaTranscription(projectId) {
    return invoke('media-learning', { action: 'retry-transcription', projectId })
  },
  translateMediaProject(projectId) {
    return invoke('media-learning', { action: 'translate-project', projectId, restart: true })
  },
  setMediaTranslationEnabled(projectId, enabled) {
    return invoke('media-learning', { action: 'translation-toggle', projectId, enabled })
  },
  rebuildMediaTimeline(projectId) {
    return invoke('media-learning', { action: 'timeline-rebuild', projectId })
  },
  lookupMediaWord(input) {
    return invoke('media-learning', { action: 'word-lookup', ...input })
  },
  addMediaCollection(input) {
    return invoke('media-learning', { action: 'collection-add', ...input })
  },
  previewPersonalPractice(input) {
    return invoke('media-learning', { action: 'personal-practice-preview', ...input })
  },
  commitPersonalPractice(input) {
    return invoke('media-learning', { action: 'personal-practice-commit', ...input })
  },
  async deleteMediaProject(projectId) {
    await invoke('media-learning', { action: 'delete-project', projectId })
  },
  async updateMediaSegment(input) {
    await invoke('media-learning', { ...input, segmentAction: input.action, action: 'segment-update' })
  },
  submitMediaPractice(input) {
    return invoke('media-learning', { action: 'practice-submit', ...input })
  },
  addMediaSegmentToTyping(input) {
    return invoke('media-learning', { action: 'typing-add', ...input })
  },
  async mediaSourceUrl(projectId) {
    const result = await invoke<{ url: string }>('media-learning', { action: 'source-url', projectId })
    return result.url
  },
  createMediaExport(input) {
    return invoke('media-learning', { action: 'export-create', ...input })
  },
  mediaJob(jobId) {
    return invoke('media-learning', { action: 'job', jobId })
  },
  cancelMediaJob(jobId) {
    return invoke('media-learning', { action: 'job-cancel', jobId })
  },
  async mediaExportUrl(jobId) {
    const result = await invoke<{ url: string }>('media-learning', { action: 'export-url', jobId })
    return result.url
  },
}
